import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { validationActions } from '../actions/modelsActions/validationActions'
import _cloneDeep from 'lodash/cloneDeep'
import { blockDictionaryActions } from '../actions/modelsActions/blockDictionaryActions'
import { inputActions } from '../actions/modelsActions/inputActions'
import { fetchProductSchemaAndConfig } from '../async-thunks/configurations/fetchProductSchemaAndConfig'
import { resetProductSchemaAndConfig } from '../async-thunks/configurations/resetProductSchemaAndConfig'
import { applyProductConfigChanges } from '../async-thunks/configurations/applyProductConfigChanges'
import { blockArrayActions } from '../actions/modelsActions/blockArrayActions'
import { ConfigurationsActionType } from '../../types/configurations'
import { initEmptyDataAction } from '../actions/modelsActions/initEmptyData'
import _isEqual from 'lodash/isEqual'
import { uploadMultipleConfigs } from '../async-thunks/products/uploadMultipleConfigs'
import { uploadProductConfig } from '../async-thunks/products/uploadProductConfig'

export interface IConfigState {
    productsConfigurations: any
    productsConfigurationsInterim: any
    productsConfigurationsDefault: any
    configsHashMaps: Record<string, Record<string, string>>
    suggestedValues: Record<string /* suggested_values_path*/, Array<string>>
    unsaved: any
    loading: boolean
    fetchingError: boolean
    invalidFields: any
    rulesUpdating: Record<string /* element_id*/, true | false | undefined> // true = updating, false = error, undefined = success
    isChecked: any
}

const initialState: IConfigState = {
    productsConfigurations: {},
    productsConfigurationsInterim: {},
    productsConfigurationsDefault: {},
    configsHashMaps: {},
    suggestedValues: {},
    unsaved: {},
    loading: false,
    fetchingError: false,
    invalidFields: {},
    rulesUpdating: {},
    isChecked: {},
}

const configsSlice = createSlice({
    name: 'configsSlice',
    initialState,
    reducers: {
        updateConfigHashMap: (state, action: PayloadAction<{ guid: string; hashMap: any }>) => {
            const { guid, hashMap } = action.payload
            state.configsHashMaps[guid] = hashMap
        },
        disableBlock: (state, action: PayloadAction<{
            path: string[]
            errorKeyName: string
        }>) => {
            const { path, errorKeyName } = action.payload
            path.reduce((acc: any, cur: any, idx: number) => {
                if (idx === path.length - 1) {
                    if (acc) {
                        return (acc[cur] = null)
                    } else {
                        return acc
                    }
                }
                return acc[cur]
            }, state?.productsConfigurations ?? {})

            const [guid, , tabName] = path
            validationActions.cleanBlockInvalidFields(
                guid,
                tabName,
                errorKeyName,
                state.invalidFields
            )
        },

        enableBlock: (
            state,
            action: PayloadAction<{ path: string[]; data: boolean }>
        ) => {
            const { path, data } = action.payload
            path.reduce((acc: any, cur: any, idx: number) => {
                if (idx === path.length - 1) {
                    return (acc[cur] = data)
                }
                return acc[cur]
            }, state?.productsConfigurations ?? {})
        },

        checkInvalidTabs: (
            state,
            action: PayloadAction<{ guid: string; data: number }>
        ) => {
            const { guid, data } = action.payload
            state.isChecked[guid] = state.isChecked[guid] ? state.isChecked[guid] + data : 1
        },

        setConfigInvalid: (
            state,
            action: PayloadAction<{
                guid: string
                tabName: string
                errorKeyName: string
            }>
        ) => {
            const { guid, tabName, errorKeyName } = action.payload

            state.invalidFields[guid] = state.invalidFields[guid] ?? {}
            state.invalidFields[guid][tabName] = state.invalidFields[guid][tabName] ?? {}
            state.invalidFields[guid][tabName][errorKeyName] = true
        },

        setConfigValid: (
            state,
            action: PayloadAction<{
                guid: string
                tabName: string
                errorKeyName: string
            }>
        ) => {
            const { guid, tabName, errorKeyName } = action.payload

            delete state.invalidFields?.[guid]?.[tabName]?.[errorKeyName]
        },

        addBlockDictionaryItem: (
            state,
            action: PayloadAction<{ path: string[]; item: any; data: any }>
        ) => {
            const { path, item, data } = action.payload

            blockDictionaryActions.addItem(state, path, item, data)
        },

        addBlockDictionaryNewKey: (
            state,
            action: PayloadAction<{ path: string[]; oldKey: string; data: any }>
        ) => {
            const { path, oldKey, data } = action.payload

            blockDictionaryActions.newKey(state, path, data, oldKey)
        },

        deleteBlockDictionaryItem: (
            state,
            action: PayloadAction<{ path: string[]; data: any; dictionaryKey: string }>
        ) => {
            const { path, data, dictionaryKey } = action.payload

            blockDictionaryActions.deleteItem(state, path, data, dictionaryKey)
        },

        editBlockDictionaryValue: (
            state,
            action: PayloadAction<{ path: string[]; key: any; value: any }>
        ) => {
            const { path, key, value } = action.payload
            blockDictionaryActions.editDictionaryValue(state, path, key, value)
        },

        changeFlag: (
            state,
            action: PayloadAction<{
                path: string[]
                componentState: any
                data: any
            }>
        ) => {
            const { path, componentState, data } = action.payload
            inputActions.flagModify(state, path, componentState, data)
        },

        modifyInput: (
            state,
            action: PayloadAction<{
                path: string[]
                componentState: any
                data: any
                name: string
                type?: string
                isEmptyAsNull?: boolean
            }>
        ) => {
            inputActions.inputModify(
                state,
                action.payload.path,
                action.payload.componentState,
                action.payload.data,
                action.payload.name,
                action.payload.type,
                action.payload.isEmptyAsNull
            )
        },

        modifyMultiPick: (
            state,
            action: PayloadAction<{
                path: string[]
                componentState: any
                name: string
                isNullable?: boolean
                isEmptyAsNull?: boolean
            }>
        ) => {
            inputActions.multiSelectModify(
                state,
                action.payload.path,
                action.payload.componentState,
                action.payload.name,
                action.payload.isNullable,
                action.payload.isEmptyAsNull
            )
        },

        modifyDateTime: (
            state,
            action: PayloadAction<{
                path: string[]
                componentState: any
                data: any
                name: string
            }>
        ) => {
            inputActions.dateTimeModify(
                state,
                action.payload.path,
                action.payload.componentState,
                action.payload.data,
                action.payload.name
            )
        },

        modifyEnum: (
            state,
            action: PayloadAction<{
                path: string[]
                componentState: any
                data: any
            }>
        ) => {
            inputActions.enumModify(
                state,
                action.payload.path,
                action.payload.componentState,
                action.payload.data
            )
        },

        dndBlockArray: (
            state,
            action: PayloadAction<{
                path: string[]
                items: any[]
                data: any
            }>
        ) => {
            blockArrayActions.DND(
                state,
                action.payload.path,
                action.payload.items,
                action.payload.data
            )
        },

        deleteBlockArrayItem: (
            state,
            action: PayloadAction<{
                path: string[]
                index: number
                data: any
            }>
        ) => {
            blockArrayActions.deleteItem(
                state,
                action.payload.path,
                action.payload.index,
                action.payload.data
            )
        },

        deleteBlockArrayCardItem: (
            state,
            action: PayloadAction<{
                path: string[]
                arrOfItems: any[]
                errorKeyName: string
            }>
        ) => {
            const {
                path,
                arrOfItems: newArrOfItems,
                errorKeyName,
            } = action.payload
            blockArrayActions.deleteItemCard(state, path, newArrOfItems)

            const [guid, , tabName] = path
            validationActions.cleanBlockInvalidFields(
                guid,
                tabName,
                errorKeyName,
                state.invalidFields
            )
        },

        modifyBlockArrayTable: (
            state,
            action: PayloadAction<{
                path: string[]
                index: number
                data: any
            }>
        ) => {
            blockArrayActions.tableModify(
                state,
                action.payload.path,
                action.payload.index,
                action.payload.data
            )
        },

        replaceBlockArrayTable: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            const { path, data } = action.payload
            blockArrayActions.replaceTable(state, path, data)
        },

        importCsvDataToBlockArrayTable: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            const { path, data } = action.payload
            blockArrayActions.importDataFromCsv(state, path, data)
        },

        addRowOnBlockArrayTableTop: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            blockArrayActions.addItem(
                state,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_TABLE_ADD_ROW_TOP
            )
        },

        addRowOnBlockArrayTableBottom: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            blockArrayActions.addItem(
                state,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_TABLE_ADD_ROW_BOTTOM
            )
        },

        addCardToBlockArrayBeginning: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            blockArrayActions.addItem(
                state,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_CARD_ADD_TO_BEGINNING
            )
        },

        addCardToBlockArrayEnd: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            blockArrayActions.addItem(
                state,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_CARD_ADD_TO_END
            )
        },

        duplicateBlockArrayCard: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            blockArrayActions.addItem(
                state,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_CARD_DUPLICATE
            )
        },

        add100BlockArrayCards: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            blockArrayActions.addItem(
                state,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_CARD_ADD_100
            )
        },

        refreshRules: (state, action: PayloadAction<{ path: string[] }>) => {
            blockArrayActions.refreshRules(state, action.payload.path)
        },

        applyRulesPartialUpdate: (state, action) => {
            const { path, data } = action.payload
            blockArrayActions.updateRulesPartial(state, path, data)
        },

        setRulesUpdating: (state, action: PayloadAction<string[]>) => {
            const elementIds = action.payload
            state.rulesUpdating = elementIds.reduce((acc: any, cur: string) => {
                acc[cur] = true
                return acc
            }, state.rulesUpdating)
        },

        setRulesUpdatingError: (state, action: PayloadAction<string[]>) => {
            const elementIds = action.payload as string[]
            const rulesUpdating = elementIds.reduce((acc: any, cur: string) => {
                acc[cur] = false
                return acc
            }, state.rulesUpdating)

            state.rulesUpdating = rulesUpdating
        },

        setRulesUpdatingSuccess: (state, action: PayloadAction<string[]>) => {
            const elementIds = action.payload as string[]
            elementIds.forEach((elementId) => {
                delete state.rulesUpdating[elementId]
            })
        },

        initEmptyConfig: (
            state,
            action: PayloadAction<{ guid: string, name: string, data: any }>
        ) => {
            const { guid, name, data } = action.payload
            state.productsConfigurations[guid].configuration[name] = data
        },

        initEmptyData: (state, action) => {
            initEmptyDataAction(
                state,
                action.payload.path,
                action.payload.name,
                action.payload.schema,
                action.payload?.type
            )
        },

        setNewRuleName: (state, action: PayloadAction<{ path: string[], ruleName: string }>) => {
            const { path, ruleName } = action.payload
            inputActions.ruleNameModify(state, path, ruleName)
        },

        changeRuleState: (
            state,
            action: PayloadAction<{ path: string[]; isRuleActive: boolean }>
        ) => {
            inputActions.changeRuleState(
                state,
                action.payload.path,
                action.payload.isRuleActive
            )
        },

        checkUnsavedChanges: (state, action: PayloadAction<{guid: string}>) => {
            const { guid } = action.payload
            const changesSaved = _isEqual(
                state.productsConfigurations[guid],
                state.productsConfigurationsDefault[guid]
            )

            state.unsaved[guid] = !changesSaved
        },

        modifyConfigByPath: (state, action: PayloadAction<{ path: string[], data: any }>) => {
            const { path, data } = action.payload
            path.reduce((acc: any, cur: string, idx: number) => {
                if (idx === path.length - 1 && acc && acc[cur]) {
                    acc[cur] = data
                }
                return acc?.[cur]
            }, state.productsConfigurations ?? {})
        },

        modifyConfig: (state, action: PayloadAction<{path: string[], data: any}>) => {
            const { path, data } = action.payload
            path.reduce((acc: any, cur: string, idx: number) => {
                if (idx === path.length - 1) {
                    acc[cur] = data
                }
                return acc[cur]
            }, state.productsConfigurations ?? {})
        },

        addSuggestedValue: (state, action: PayloadAction<{
            suggestedValuesPath: string,
            value: string
        }>) => {
            const { suggestedValuesPath, value } = action.payload
            if (!Array.isArray(state.suggestedValues[suggestedValuesPath])) {
                state.suggestedValues[suggestedValuesPath] = []
            }
            state.suggestedValues[suggestedValuesPath].push(value)
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductSchemaAndConfig.pending, (state) => {
                state.loading = true
                state.fetchingError = false
            })
            .addCase(fetchProductSchemaAndConfig.rejected, (state) => {
                state.loading = false
                state.fetchingError = true
            })
            .addCase(fetchProductSchemaAndConfig.fulfilled, (state, action) => {
                const {
                    data: configuration,
                    guid,
                    forceUpdate,
                } = action.payload

                if (state.productsConfigurations[guid] && !forceUpdate) {
                    state.loading = false
                    return
                }

                const hashMap = configuration?.configuration_hash_map
                const configCloneInterim = _cloneDeep(configuration)
                const configCloneDefault = _cloneDeep(configuration)

                state.configsHashMaps[guid] = hashMap
                state.productsConfigurations[guid] = configCloneDefault
                state.productsConfigurationsInterim[guid] = configCloneInterim
                state.productsConfigurationsDefault[guid] = configCloneDefault
                state.invalidFields[guid] = {}
                state.loading = false
                state.fetchingError = false
            })
            .addCase(resetProductSchemaAndConfig.pending, (state) => {
                state.loading = true
                state.fetchingError = false
            })
            .addCase(resetProductSchemaAndConfig.rejected, (state) => {
                state.loading = false
                state.fetchingError = true
            })
            .addCase(resetProductSchemaAndConfig.fulfilled, (state, action) => {
                const { data: configuration, guid } = action.payload

                const configCloneInterim = _cloneDeep(configuration)
                const configCloneDefault = _cloneDeep(configuration)

                state.productsConfigurations[guid] = configCloneDefault
                state.productsConfigurationsInterim[guid] = configCloneInterim
                state.productsConfigurationsDefault[guid] = configCloneDefault
                state.invalidFields[guid] = {}
                state.isChecked[guid] = 0
                state.loading = false
                state.fetchingError = false
            })
            .addCase(applyProductConfigChanges.pending, (state) => {
                state.loading = true
            })
            .addCase(applyProductConfigChanges.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(applyProductConfigChanges.rejected, (state) => {
                state.loading = false
            })
            .addCase(uploadMultipleConfigs.pending, (state) => {
                state.loading = true
            })
            .addCase(uploadMultipleConfigs.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(uploadMultipleConfigs.rejected, (state) => {
                state.loading = false
            })
            .addCase(uploadProductConfig.pending, (state) => {
                state.loading = true
            })
            .addCase(uploadProductConfig.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(uploadProductConfig.rejected, (state) => {
                state.loading = false
            })
    },
})

export const { actions: configsActions } = configsSlice
export const { reducer: configsReducer } = configsSlice